import { useEffect, useState } from "react"
import { publicConsumerApi } from "@/lib/api/public-consumer"
import type { ApiEstimate } from "@/lib/api/types"

// Polls the public estimate read until the damage agent has written line
// items, then resolves "ready". Mirrors the authed use-agent-polling pattern
// but against /public/estimates/:trackToken and resolves on "line items exist"
// (NOT on cost) — the consumer reveal must never wait for a price.
//
// The orchestrator owns this hook (not the reveal): polling starts when the
// photos are appended (`enabled`) and keeps running underneath the extras so
// the "Reading your photos…" pill and the reveal read one stream. A bumped
// reloadNonce (post-reveal correction) restarts from scratch.

export type ConsumerPollStatus = "idle" | "polling" | "ready" | "timed-out"

export function useConsumerEstimatePolling(
  trackToken: string | null,
  opts: {
    enabled?: boolean
    ceilingMs?: number
    intervalMs?: number
    reloadNonce?: number
  } = {},
) {
  const { enabled = true, ceilingMs = 30_000, intervalMs = 1_500, reloadNonce = 0 } = opts
  const [estimate, setEstimate] = useState<ApiEstimate | null>(null)
  const [status, setStatus] = useState<ConsumerPollStatus>("idle")

  useEffect(() => {
    if (!trackToken || !enabled) {
      setStatus("idle")
      return
    }
    setStatus("polling")
    let alive = true
    let timer: ReturnType<typeof setTimeout>
    const start = Date.now()

    const tick = async () => {
      try {
        const est = await publicConsumerApi.getEstimate(trackToken)
        if (!alive) return
        setEstimate(est)
        if ((est.lineItems?.length ?? 0) > 0) {
          setStatus("ready")
          return
        }
      } catch {
        // Swallow transient errors and keep polling until the ceiling.
        if (!alive) return
      }
      if (Date.now() - start > ceilingMs) {
        setStatus("timed-out")
        return
      }
      timer = setTimeout(tick, intervalMs)
    }

    void tick()
    return () => {
      alive = false
      clearTimeout(timer)
    }
  }, [trackToken, enabled, ceilingMs, intervalMs, reloadNonce])

  return { estimate, status }
}
